import { createAgentWorkspaceFakeRuntime, FAKE_ACCOUNT_ID } from "./fakeRuntime";
import { composeWorkspaceState, WORKSPACE_PRODUCT_STATES } from "./workspaceStateStore";
import {
  AUTHORIZATION_OUTCOMES,
  FAKE_AUTHORIZATION_DECISIONS,
  WORKSPACE_SEMANTIC_ACTIONS,
  composeAuthorizationPresentation,
  captureSemanticIntent,
} from "./authorizationComposition";
import {
  CONTROLLER_STATES,
  FAKE_CONTROLLER_EVIDENCE,
  createControllerGenerationRegistry,
  createAttemptRegistry,
  evaluateAttemptAdmission,
} from "./controllerFenceComposition";
import { WORKSPACE_TOOLS, WORKSPACE_TOOL_PROJECTION_STATUSES, resolveWorkspaceToolProjection } from "./projectionContract";
import { SCRIPT_RUN_IDENTITY_FIXTURE } from "./workspaceFixtures";

function deepFreeze(value) {
  if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
  Object.freeze(value);
  Object.values(value).forEach(deepFreeze);
  return value;
}

// Composes each of the five reviewed WORKSPACE_TOOLS' fake envelopes
// (WORKSPACE-AUDIT-007 §13 item 4) into a presentation-ready
// {status, reason, data} per tool, using the same Account-scope/freshness
// evaluation the coarse chain already applies.
//
// Item 7 correction: gates on the OUTER workspaceState.accountId validity
// first, matching composeScriptProjectionStatus's existing pattern. Without
// this gate, a broken overall Account context (e.g. the crossAccount
// scenario, where composeWorkspaceState already fails closed with
// accountId: null) was masked here: `accountId` falls back to
// FAKE_ACCOUNT_ID for authorization-composition purposes, and every
// fixture-sourced tool projection's own accountId is ALSO FAKE_ACCOUNT_ID,
// so the per-tool cross-account check in resolveWorkspaceToolProjection
// never fired — Lead/Discovery/Quote/Calendar/Notes silently continued to
// render real fixture data even though the Account context itself was
// already known-invalid. BOUNDARY-A-APPROVAL-001 §4 "Cross-Account
// mismatch fails closed and clears protected composition" and ENG-008 §4
// "Missing or prohibited information is not inferred from another domain"
// both require the opposite: when the Account context itself cannot be
// trusted, no tool projection may proceed to disclose its data.
function composeToolProjections(toolProjections, { workspaceState, accountId, logicalNow }) {
  const result = {};
  const accountContextValid = Boolean(workspaceState.accountId);
  Object.values(WORKSPACE_TOOLS).forEach((tool) => {
    result[tool] = accountContextValid
      ? resolveWorkspaceToolProjection(tool, toolProjections?.[tool], {
          expectedAccountId: accountId,
          logicalNow,
        })
      : {
          status: WORKSPACE_TOOL_PROJECTION_STATUSES.UNKNOWN,
          reason: workspaceState.diagnostic || "account-invalid",
          data: null,
        };
  });
  return deepFreeze(result);
}

// Outer Account-context gate only (WORKSPACE-AUDIT-007 §13 item 4) —
// whether the Workspace's own Account context is itself valid. This
// stays exactly as item 4 built it, unchanged: it does not by itself
// determine whether a Script/Run is assigned or attached. The item 6
// candidate-vs-attached admission model below is the second, inner gate
// that only runs once this outer gate has already passed.
function composeScriptProjectionStatus(workspaceState) {
  if (!workspaceState.accountId) {
    return deepFreeze({
      status: WORKSPACE_TOOL_PROJECTION_STATUSES.UNKNOWN,
      reason: workspaceState.diagnostic || "account-invalid",
    });
  }
  return deepFreeze({ status: WORKSPACE_TOOL_PROJECTION_STATUSES.OK, reason: null });
}

export const SCRIPT_RUN_ADMISSION_STATUSES = Object.freeze({
  MISSING: "missing",
  CANDIDATE: "candidate",
  PENDING: "pending",
  ATTACHED: "attached",
  UNKNOWN: "unknown",
});

// WORKSPACE-AUDIT-007 §13 item 6: Script/Run candidate-vs-attached
// admission state. Interprets the EXISTING coarse script/run projections'
// assignmentState/runState fields (their coarse PROJECTION_FIELD_ALLOWLISTS
// entries are unchanged — this only reads them, it does not widen what
// they may carry) into the model WORKSPACE-RUNTIME-001 §105 requires:
//   - missing: assignmentState is explicitly "no-match" — SCRIPT-RUNTIME-001
//     §29 No-Match Handling: "No match is a first-class result... must not
//     choose an arbitrary Script." This is the ONLY trigger for missing.
//   - candidate: "Before Run admission, Script may show a clearly labeled
//     candidate resolved from assignment without editable execution
//     controls" (§105). No run_id — RUN-RUNTIME-001 §34 makes run_id
//     mandatory only for active/resumed execution, which a candidate is
//     not.
//   - pending: "Delayed attachment remains pending without creating local
//     progress" (§105); RUN-RUNTIME-001 §33 Creation Unknown — "a new
//     identity is prohibited until the outcome is known."
//   - attached: "After authoritative attachment, it renders the immutable
//     publication and current Run cursor" (§105) — carries the immutable
//     run_id/publication/content-version/integrity-evidence binding
//     (RUN-RUNTIME-001 §34, §37).
//   - unknown: everything else — an absent/undefined projection (broken or
//     absent wiring), an unrecognized assignmentState, or any explicitly
//     indeterminate/unsafe Assignment outcome (SCRIPT-RUNTIME-001 §25
//     Assignment Input: "Missing or conflicting mandatory facts yield
//     non-selection, denial, or Unknown rather than permissive fallback";
//     §30 Conflicting Assignment). None of these are coerced into missing
//     (a specific, first-class "no Script" outcome) or into a usable
//     candidate/Run — WORKSPACE-RUNTIME-001 §105 "Unknown admission or
//     transition makes execution read-only until reconciled";
//     SCRIPT-RUNTIME-001 §35 "Live-graph substitution is prohibited."
// Once attached, `candidate` is not also returned — RUN-RUNTIME-001 §37's
// immutable publication binding is the current identity; the
// pre-attachment candidate is superseded, not retained alongside it.
export function resolveScriptRunAdmission({ scriptProjection, runProjection, identity }) {
  const assignmentState = scriptProjection?.data?.assignmentState;

  if (assignmentState === "no-match") {
    return deepFreeze({
      status: SCRIPT_RUN_ADMISSION_STATUSES.MISSING,
      reason: "no-script-assignment",
      candidate: null,
      run: null,
    });
  }

  if (assignmentState !== "resolved") {
    return deepFreeze({
      status: SCRIPT_RUN_ADMISSION_STATUSES.UNKNOWN,
      reason: "assignment-state-indeterminate",
      candidate: null,
      run: null,
    });
  }

  const runState = runProjection?.data?.runState;
  switch (runState) {
    case "absent":
      return deepFreeze({
        status: SCRIPT_RUN_ADMISSION_STATUSES.CANDIDATE,
        reason: null,
        candidate: identity.candidate,
        run: null,
      });
    case "pending":
      return deepFreeze({
        status: SCRIPT_RUN_ADMISSION_STATUSES.PENDING,
        reason: "run-attachment-pending",
        candidate: identity.candidate,
        run: null,
      });
    case "attached":
      return deepFreeze({
        status: SCRIPT_RUN_ADMISSION_STATUSES.ATTACHED,
        reason: null,
        candidate: null,
        run: identity.attached,
      });
    default:
      return deepFreeze({
        status: SCRIPT_RUN_ADMISSION_STATUSES.UNKNOWN,
        reason: "run-admission-unknown",
        candidate: null,
        run: null,
      });
  }
}

export const QUARANTINE_REASONS = Object.freeze({
  RECONCILIATION_REQUIRED: "reconciliation-required",
  TELEPHONY_EVIDENCE_UNKNOWN: "telephony-evidence-unknown",
  RECONNECTING: "reconnecting",
  AUTHORIZATION_UNKNOWN: "authorization-unknown",
  CONTROLLER_FENCE_UNKNOWN: "controller-fence-unknown",
});

// ENG-009 §3 "quarantine dependent controls while relevant authority is
// Unknown" / §4 "Unknown does not authorize blind retry, command replay, or
// optimistic success" and "Reconnecting communicates recovery in progress;
// it is not restored authority." The dependent control this governs is the
// Workspace status control's WORKSPACE_SEMANTIC_ACTIONS.DIALER_OPEN
// admission — the one real admission-gated action the fake contract
// already models (authorizationComposition.js/controllerFenceComposition.js,
// ENG-005/ENG-006), and which no component previously consumed. Quarantine
// is derived only from already-composed facts already present on this view
// model; it invents no new Telephony/provider evidence. Scoped narrowly to
// Unknown-classified and Reconnecting outcomes only — a definitive deny/
// revoked/stale outcome is not "quarantine," it is already-correct
// fail-closed denial from ENG-005/ENG-006 and must not be relabeled;
// Disconnected is a definitive KNOWN fact, not an unresolved one, so it is
// not a quarantine trigger either.
function resolveDependentControlQuarantine({ condition, productState, authorization, attempt }) {
  if (condition === "reconciliation-required") {
    return deepFreeze({ quarantined: true, reason: QUARANTINE_REASONS.RECONCILIATION_REQUIRED });
  }
  if (condition === "unknown") {
    return deepFreeze({ quarantined: true, reason: QUARANTINE_REASONS.TELEPHONY_EVIDENCE_UNKNOWN });
  }
  if (productState === WORKSPACE_PRODUCT_STATES.RECONNECTING) {
    return deepFreeze({ quarantined: true, reason: QUARANTINE_REASONS.RECONNECTING });
  }
  if (authorization.admission.outcome === AUTHORIZATION_OUTCOMES.UNKNOWN) {
    return deepFreeze({ quarantined: true, reason: QUARANTINE_REASONS.AUTHORIZATION_UNKNOWN });
  }
  if (attempt?.fence?.state === CONTROLLER_STATES.UNKNOWN) {
    return deepFreeze({ quarantined: true, reason: QUARANTINE_REASONS.CONTROLLER_FENCE_UNKNOWN });
  }
  return deepFreeze({ quarantined: false, reason: null });
}

const STATUS_COPY = Object.freeze({
  "Logged Out": "No active campaign session. Select a campaign and choose Login to preview participation.",
  Ready: "Campaign login is complete. Dialing has not started.",
  Starting: "A campaign login intent is pending authoritative confirmation.",
  Active: "Dialing participation is confirmed by the fake runtime evidence.",
  Paused: "Dialing is paused while campaign participation remains established.",
  Reconnecting: "Recovery is in progress. Do not rely on the prior confirmed status.",
  Unknown: "Required evidence is indeterminate. Dependent controls remain unsafe.",
  Ending: "A campaign logout intent is pending authoritative confirmation.",
  Disconnected: "The required connection is confirmed absent or unusable.",
});

// Presentation tone only — never a substitute for the canonical product-state
// label itself (WORKSPACE-STATE-001 §6 forbidden equivalences). Consumers
// must always pair a tone with the text label; tone never carries meaning
// alone (WORKSPACE-FEATURE-001 §20.3).
const STATUS_TONE = Object.freeze({
  "Logged Out": "neutral",
  Ready: "info",
  Starting: "pending",
  Active: "success",
  Paused: "warning",
  Reconnecting: "pending",
  Unknown: "critical",
  Ending: "pending",
  Disconnected: "critical",
});

/**
 * Composes one deterministic, effect-free presentation view-model from the
 * existing Boundary A fake runtime, canonical Workspace state composition
 * (ENG-004), authorization composition (ENG-005), and controller/fence/
 * attempt composition (ENG-006). This module performs no rendering, no
 * network, and no domain effect; it only derives the facts ENG-007
 * presentation is required to display.
 */
export function composeWorkspaceViewModel({
  scenarioName = "ready",
  authorizationDecisionKey = "allow",
  controllerEvidenceKey = "generationOneCurrent",
  controllerCurrentOverride = null,
} = {}) {
  const runtime = createAgentWorkspaceFakeRuntime({ scenarioName });
  const snapshot = runtime.read();
  const workspaceState = composeWorkspaceState(snapshot.projections, snapshot.logicalNow);

  const accountId = workspaceState.accountId || FAKE_ACCOUNT_ID;
  const action = WORKSPACE_SEMANTIC_ACTIONS.DIALER_OPEN;
  const decision = FAKE_AUTHORIZATION_DECISIONS[authorizationDecisionKey] || null;

  const authorization = composeAuthorizationPresentation({
    authorizationProjection: snapshot.projections.authorization,
    decision,
    accountId,
    action,
    logicalNow: snapshot.logicalNow,
  });

  const controllerEvidence = FAKE_CONTROLLER_EVIDENCE[controllerEvidenceKey] || null;
  const controllerRegistry = createControllerGenerationRegistry();
  const seed =
    controllerCurrentOverride ||
    (controllerEvidence?.generation
      ? { controllerId: controllerEvidence.controllerId, generation: controllerEvidence.generation }
      : null);
  if (seed) {
    controllerRegistry.registerGeneration({
      accountId,
      controllerId: seed.controllerId,
      generation: seed.generation,
    });
  }

  const attemptRegistry = createAttemptRegistry();
  const intent = decision
    ? captureSemanticIntent({
        action,
        resource: decision.resource,
        decision,
        logicalNow: snapshot.logicalNow,
      })
    : null;

  const attempt = intent
    ? evaluateAttemptAdmission({
        accountId,
        actor: decision.actor,
        action,
        resource: decision.resource,
        intent,
        decision,
        controllerEvidence,
        controllerRegistry,
        attemptRegistry,
        logicalNow: snapshot.logicalNow,
      })
    : null;

  const productState = workspaceState.productState;

  const toolProjections = composeToolProjections(snapshot.toolProjections, {
    workspaceState,
    accountId,
    logicalNow: snapshot.logicalNow,
  });
  const scriptProjectionStatus = composeScriptProjectionStatus(workspaceState);
  const scriptRunAdmission = resolveScriptRunAdmission({
    scriptProjection: workspaceState.projections.script,
    runProjection: workspaceState.projections.run,
    identity: SCRIPT_RUN_IDENTITY_FIXTURE,
  });
  const quarantine = resolveDependentControlQuarantine({
    condition: workspaceState.condition,
    productState,
    authorization,
    attempt,
  });

  return Object.freeze({
    scenarioName,
    logicalNow: snapshot.logicalNow,
    accountId,
    accountEpoch: workspaceState.accountEpoch,
    workspaceState,
    productState,
    condition: workspaceState.condition,
    diagnostic: workspaceState.diagnostic,
    statusDescription: STATUS_COPY[productState] || "Status evidence is not currently mapped.",
    statusTone: STATUS_TONE[productState] || "neutral",
    authorization,
    controllerEvidence,
    attempt,
    toolProjections,
    scriptProjectionStatus,
    scriptRunAdmission,
    quarantine,
  });
}
